import { AuthGuard } from './guards/auth.guard';
import { ConfirmationRespondentsDetailComponent } from './user/confirmation-respondents/confirmation-respondents-detail/confirmation-respondents-detail.component';
import { ConfirmationRespondentsListComponent } from './user/confirmation-respondents/confirmation-respondents-list.component';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HttpClientModule,
  HttpClient,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';

import { SortablejsModule } from 'ngx-sortablejs';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { ModalModule } from 'ngx-bootstrap/modal';
import { AppRoutingModule } from './app-routing.module';
import { CookieService } from 'ngx-cookie-service';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';

import { ConfigService } from './services/config.service';
import { LanguageService } from './services/language.service';
import { CategoriesService } from './services/categories.service';
import { UserService } from './services/user.service';
import { FeedbackService } from './services/feedback.service';
import { SurveyService } from './services/survey.service';
import { BusinessUnitService } from './services/business-unit.service';
import { EventNotificationService } from './services/event-notification.service';
import { CustomIntercepter } from './services/customIntercepter.service';

import { AdminModule } from './admin/admin.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './common/header/header.component';
import { BreadcrumbComponent } from './common/breadcrumb/breadcrumb.component';
import { UsermenuComponent } from './common/usermenu/usermenu.component';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { SurveyListComponent } from './user/survey-list/survey-list.component';
import { FeedbackChatComponent } from './user/feedback-chat/feedback-chat.component';
import { SurveyComponent } from './user/survey/survey.component';
import { MyTeamComponent } from './user/my-team/my-team.component';
import { UserQuestionFormSelectorComponent } from './user/survey/questions/user-question-form-selector.component';
import { TextFormComponent } from './user/survey/questions/text-form/text-form.component';
import { CheckboxRadioFormComponent } from './user/survey/questions/checkbox-radio-form/checkbox-radio-form.component';
import { MatrixFormComponent } from './user/survey/questions/matrix-form/matrix-form.component';
import { MyPageComponent } from './user/my-page/my-page.component';
import { MyReportsComponent } from './user/my-page/my-reports/my-reports.component';
import {
  BsDatepickerConfig,
  BsDatepickerModule,
} from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { NgxSelectModule } from 'ngx-select-ex';
import { FbtCommonModule } from './common/common.module';
import { ConfirmationRespondentService } from './services/confirmation-respondent.service';
import { registerLocaleData } from '@angular/common';
import { RespondentsApprovalSearchPageComponent } from './user/respondents-approval-search-page/respondents-approval-search-page.component';
import { MyBuComponent } from './user/my-bu/my-bu.component';
import { ToastrModule } from 'ngx-toastr';
import { RespondentsApprovalSearchPageHeaderComponent } from './user/respondents-approval-search-page-header/respondents-approval-search-page-header.component';
import { RespondentsApprovalBusinessUnitSearchPageComponent } from './user/respondents-approval-business-unit-search-page/respondents-approval-business-unit-search-page.component';
import { Except360ConfirmationRespondentsDetailComponent } from './user/confirmation-respondents/except360-confirmation-respondents-detail/except360-confirmation-respondents-detail.component';
import { FeedbackChatBodyComponent } from './user/feedback-chat/feedback-chat-body/feedback-chat-body.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { BuReportsComponent } from './user/my-bu/bu-reports/bu-reports.component';
import { FeedBackChatReportComponent } from './user/my-bu/bu-reports/feedback-chat-report/feedback-chat-report.component';
import { ProcessReportComponent } from './user/my-bu/bu-reports/process-report/process-report.component';
import { LinksCard } from './common/links-card/links-card.component';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { EmojiModule } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ProcessInfoReportComponent } from './user/my-bu/bu-reports/process-info-report/process-info-report.component';
import { IdentityModule } from './identity.module';
import { LoginService } from './services/login.service';

registerLocaleData(localeRu, 'ru', localeRuExtra);
defineLocale('en-gb', enGbLocale);


export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
export function getDatepickerConfig(): BsDatepickerConfig {
  return Object.assign(new BsDatepickerConfig(), {
    showWeekNumbers: false,
    dateInputFormat: 'DD.MM.YYYY',
  });
}

export function initApplication(
  configService: ConfigService,
  loginService: LoginService
): Function {
  return async () => {    
    await configService.getConfigVar();
    await configService.getAllLanguages();
    await loginService.trySetCurrentUser();
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SurveyComponent,
    HeaderComponent,
    BreadcrumbComponent,
    UsermenuComponent,
    SurveyListComponent,
    FeedbackChatComponent,
    MyTeamComponent,
    UserQuestionFormSelectorComponent,
    TextFormComponent,
    CheckboxRadioFormComponent,
    MatrixFormComponent,
    MyPageComponent,
    MyReportsComponent,
    ConfirmationRespondentsListComponent,
    ConfirmationRespondentsDetailComponent,
    RespondentsApprovalSearchPageComponent,
    MyBuComponent,
    RespondentsApprovalSearchPageHeaderComponent,
    RespondentsApprovalBusinessUnitSearchPageComponent,
    Except360ConfirmationRespondentsDetailComponent,
    FeedbackChatBodyComponent,
    BuReportsComponent,
    FeedBackChatReportComponent,
    ProcessReportComponent,
    LinksCard,
    ProcessInfoReportComponent,
  ],
  imports: [
    BrowserModule,
    NgxSelectModule,
    NgxPageScrollModule,
    NgxPageScrollCoreModule.forRoot({ duration: 50 }),
    AdminModule,
    AppRoutingModule,
    ModalModule.forRoot(),
    TooltipModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
    }),
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    FbtCommonModule,
    BsDropdownModule,
    BrowserAnimationsModule,
    CollapseModule.forRoot(),
    SortablejsModule.forRoot({
      animation: 200,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ClickOutsideModule,
    BsDatepickerModule.forRoot(),
    PickerModule,
    EmojiModule,
    IdentityModule.forRoot({isOpenId: true}),
  ],
  providers: [
    ConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: initApplication,
      deps: [ConfigService, LoginService],
      multi: true,
    },
    { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
    { provide: HTTP_INTERCEPTORS, useClass: CustomIntercepter, multi: true },
    CategoriesService,
    LanguageService,
    UserService,
    CookieService,
    SurveyService,
    FeedbackService,
    BusinessUnitService,
    ConfirmationRespondentService,
    EventNotificationService,
    LanguageService,
    LoginService,
    AuthGuard,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
